/**
 * Connector Configuration Validation Schema
 * Provides input validation before API calls
 */

import { z } from 'zod';

export const ConnectorConfigValidationSchema = z.object({
  connectorId: z.number().int().positive('Connector ID must be a positive number'),
  name: z.string().min(3, 'Name must be at least 3 characters').max(100, 'Name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  baseUrl: z.string().url('Base URL must be a valid URL').optional(),
  apiEndPoint: z.string().optional(),
  timeout: z.number().int().min(1000, 'Timeout must be at least 1000ms').max(300000, 'Timeout must not exceed 300000ms').optional(),
  retryCount: z.number().int().min(0, 'Retry count must be at least 0').max(10, 'Retry count must not exceed 10').optional(),
  credentialID: z.number().int().nullable().optional(),
  agentId: z.number().int().nullable().optional(),
  knowledgeBaseId: z.number().int().nullable().optional(),
  channelId: z.number().int().nullable().optional(),
  llmModelId: z.number().int().nullable().optional(),
  agentFunctionId: z.number().int().nullable().optional(),
});

export type ConnectorConfigValidation = z.infer<typeof ConnectorConfigValidationSchema>;

export class ValidationError extends Error {
  constructor(
    public fieldErrors: Record<string, string[]>,
    message: string = 'Validation failed'
  ) {
    super(message);
    this.name = 'ValidationError';
  }

  getFieldError(field: string): string | undefined {
    return this.fieldErrors[field]?.[0];
  }

  getAllErrors(): string[] {
    return Object.values(this.fieldErrors).flat();
  }
}

export function validateConnectorConfig(data: unknown): ConnectorConfigValidation {
  try {
    return ConnectorConfigValidationSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors: Record<string, string[]> = {};
      error.issues.forEach(issue => {
        const path = issue.path.join('.');
        if (!fieldErrors[path]) {
          fieldErrors[path] = [];
        }
        fieldErrors[path].push(issue.message);
      });
      throw new ValidationError(fieldErrors, 'Connector configuration validation failed');
    }
    throw error;
  }
}

export function validateRequiredFields(
  connectorId: unknown,
  name: unknown,
  baseUrl?: unknown
): { valid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!connectorId || typeof connectorId !== 'number') {
    errors.connectorId = 'Connector ID is required and must be a number';
  }

  if (!name || typeof name !== 'string' || name.trim().length === 0) {
    errors.name = 'Name is required and must be a non-empty string';
  }

  if (baseUrl && typeof baseUrl !== 'string') {
    errors.baseUrl = 'Base URL must be a string';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
